import { useEffect, useRef, useState } from 'react'
import Button from '@ui/Button'
import Breadcrumb from '@common/Breadcrumb'
import {ROUTES,SITE } from '@/constants'
import TransformBanner from '@sections/TransformBanner'
import MetaInfo from '@common/MetaInfo'
import { useTreeAnimation } from '@helpers/useIntersectionObserver'
import { metaTitleFor } from '../assets/js/navBreadcrumb'
const { label, pageDesc, metaDesc } = metaTitleFor(ROUTES.EGOVERNANCE)


// row / col = grid start position of each diamond tile (6×6 grid, every tile spans 2×2)
const GOV_GALLERY = [
  { src: '/assets/images/client-work/1.jpg',  alt: '1',  row: 1, col: 1 },
  { src: '/assets/images/client-work/2.jpg',  alt: '2',  row: 1, col: 3 },
  { src: '/assets/images/client-work/3.jpg',  alt: '3',  row: 1, col: 5 },
  { src: '/assets/images/client-work/4.jpg',  alt: '4',  row: 2, col: 2 },
  { src: '/assets/images/client-work/5.jpg',  alt: '5',  row: 2, col: 4 },
  { src: '/assets/images/client-work/6.jpg',  alt: '6',  row: 3, col: 1 },
  { src: '/assets/images/client-work/7.jpg',  alt: '7',  row: 3, col: 3 },
  { src: '/assets/images/client-work/8.jpg',  alt: '8',  row: 3, col: 5 },
  { src: '/assets/images/client-work/9.jpg',  alt: '9',  row: 4, col: 2 },
  { src: '/assets/images/client-work/10.jpg', alt: '10', row: 4, col: 4 },
  { src: '/assets/images/client-work/11.jpg', alt: '11', row: 5, col: 1 },
  { src: '/assets/images/client-work/12.jpg', alt: '12', row: 5, col: 3 },
  { src: '/assets/images/client-work/13.jpg', alt: '13', row: 5, col: 5 },
]

const GOV_STATS = [
  { value: '20+',    label: 'Large Scale PSU Projects' },
  { value: '13+',    label: 'Years of Experience in eGovernance' },
  { value: '30 Cr+', label: 'Consumers' },
  { value: '100%',   label: 'Client Satisfaction' },
]
const GOV_JOURNEY = [
  { icon: 'fa-landmark',         title: 'Programme', desc: 'Define objectives, stakeholders, processes and the outcomes the programme needs to measure.' },
  { icon: 'fa-laptop-code',      title: 'Digital Platform', desc: 'Design secure, scalable and accessible platforms that bring workflows, departments and data together.' },
  { icon: 'fa-map-location-dot', title: 'Field Data & Operations', desc: 'Equip field teams with mobile, offline-ready applications to capture accurate data at source.' },
  { icon: 'fa-chart-line',       title: 'Real-Time Intelligence', desc: 'Turn incoming data into live dashboards, alerts and analytics that show what is happening as it happens.' },
  { icon: 'fa-scale-balanced',   title: 'Decision Making', desc: 'Give officials trusted, timely insights to monitor progress, spot issues early and act with confidence.' },
  { icon: 'fa-people-group',     title: 'Citizen & Stakeholder Experience', desc: 'Deliver transparent, multilingual and easy-to-use services that keep citizens and stakeholders informed.' },
]

const FOCUS_RATIO = 0.6 // fraction of the viewport height where a step lights up
const FADE_PX = 48      // soft tail at the leading end of the line




function ScrollTimeline({ steps }) {
  const listRef = useRef(null)
  const [isEnhanced, setIsEnhanced] = useState(false)

  useEffect(() => {
    const list = listRef.current
    if (!list) return

    const stepEls = Array.from(list.querySelectorAll('.scroll-timeline-step'))
    if (!stepEls.length) return

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let dotCenters = []
    let frame = 0

    // Dot positions relative to the list don't change on scroll, so measure only on mount / resize
    const measure = () => {
      const listTop = list.getBoundingClientRect().top
      dotCenters = stepEls.map((el) => {
        const dot = el.querySelector('.scroll-timeline-dot').getBoundingClientRect()
        return dot.top - listTop + dot.height / 2
      })
      const length = dotCenters[dotCenters.length - 1] - dotCenters[0]
      list.style.setProperty('--timeline-start', `${dotCenters[0]}px`)
      list.style.setProperty('--timeline-length', `${length}px`)
    }

    const render = () => {
      frame = 0
      const start = dotCenters[0]
      const length = dotCenters[dotCenters.length - 1] - start
      const focusLine = window.innerHeight * FOCUS_RATIO - list.getBoundingClientRect().top
      const fill = reduceMotion
        ? length
        : Math.min(Math.max(focusLine + FADE_PX - start, 0), length)

      list.style.setProperty('--timeline-fill', `${fill}px`)
      list.style.setProperty('--timeline-fade', `${Math.min(FADE_PX, length - fill)}px`)

      if (!reduceMotion) {
        stepEls.forEach((el, i) => el.classList.toggle('is-active', dotCenters[i] <= focusLine))
      }
    }

    const requestRender = () => {
      if (!frame) frame = requestAnimationFrame(render)
    }

    const resizeObserver = new ResizeObserver(() => {
      measure()
      render()
    })
    resizeObserver.observe(list)
    measure()
    render()

    if (!reduceMotion) {
      setIsEnhanced(true)
      window.addEventListener('scroll', requestRender, { passive: true })
    }

    return () => {
      window.removeEventListener('scroll', requestRender)
      resizeObserver.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [])

  return (
    <ol
      ref={listRef}
      className={`scroll-timeline${isEnhanced ? ' is-enhanced' : ''}`}
      role="list"
       data-aos="fade-down"
    >
      {steps.map(({ icon, title, desc }) => (
  <li key={title} className="scroll-timeline-step" >
    <span className="scroll-timeline-dot" aria-hidden="true">
      <i className={`fa-solid ${icon}`} />
    </span>
    <h3 className="scroll-timeline-title">{title}</h3>
    <p className="scroll-timeline-desc">{desc}</p>
  </li>
))}
    </ol>
  )
}


 const EGOVERNANCE_BUILD = [
  {
    title: 'Compliance Oriented Digital Platforms',
    description: 'Secured, user-friendly web and mobile applications, which adheres to all the specifications of security standards and compliances as outlined in VAPT, GIGW, DBIM, STQC or any other latest guidelines as specified by the government of India, from time to time.',
  },
  {
    title: 'Real-Time Government Dashboards',
    description: `Transform complex programme data into live, actionable dashboards for ministries, departments, administrators, and stakeholders.
Track KPIs, targets, progress, geographical performance, transactions, exceptions, trends, and programme outcomes through intuitive visual interfaces.`
  },
  {
    title: 'Government Websites & Digital Portals',
    description: 'Develop modern government websites and portals that combine rich user experiences with accessibility, responsiveness, multilingual content, structured information, and strong security practices.',
  },
  {
    title: 'Multilingual Digital Experiences',
    description: 'Build digital platforms that serve diverse audiences across India with multilingual interfaces, localized content, language-aware navigation, and consistent experiences across devices.',
  },
  {
    title: 'Field & Programme Management Systems',
    description: `Digitize on-ground programme execution with web and mobile applications for field teams, supervisors, agencies, and administrators.
Enable field data collection for crores of data points, task management, inspections, geo-tagging, approvals, status tracking, evidence capture, and real-time reporting.`,
  },
  {
    title: 'Analytical & Decision-Support Platforms',
    description: `Turn large volumes of programme and operational data into meaningful insights.
Our analytical platforms help stakeholders identify trends, monitor performance, compare regions, detect gaps, and make informed decisions using interactive visualizations and data-driven reporting.`,
  },
  {
    title: 'AI-Powered Government Solutions',
    description: 'Explore practical applications of AI across citizen services, information discovery, document processing, analytics, conversational interfaces, knowledge systems, workflow automation, and decision support.',
  },
  {
    title: 'Mobile Applications',
    description: 'Deliver responsive mobile experiences for citizens, government officials, field personnel, inspectors, programme managers, and other stakeholders.',
  },
  {
    title: 'Integrated Digital Ecosystems',
    description: 'Connect applications, APIs, databases, legacy systems, external platforms, and departmental systems to create a unified digital ecosystem instead of isolated applications.',
  },
]


const GOV_PARTNER_USPS = [
  'Excellent quality of work',
  'Superior Technical Team',
  'Efficient deliveries',
  'Long term reliability',
]

const DIFF_APP_DEVELOPMENT = [
  { icon: 'fa-language',      title: 'Multilingual Architecture', desc: 'Technology foundations that support multiple languages and localized content.' },
  { icon: 'fa-universal-access',  title: 'Accessibility', desc: 'Inclusive experiences designed to support diverse users and accessibility requirements.' },
  { icon: 'fa-landmark',     title: 'GIGW Alignment',   desc: 'Government websites and portals developed with applicable Guidelines for Indian Government Websites requirements in consideration.' },
  { icon: 'fa-bug',     title: 'VAPT Readiness', desc: 'Applications designed and prepared with vulnerability assessment and penetration testing requirements in mind.' },
  { icon: 'fa-palette',      title: 'DBIM Alignment',    desc: 'Digital experiences planned with relevant government digital brand and interface standards in mind.' },
  { icon: 'fa-headset',      title: 'Reliable Operations',    desc: 'Monitoring, maintenance, support, issue resolution, enhancements, and continuous improvement beyond initial deployment.' },
  { icon: 'fa-shield-halved',      title: 'Secure Engineering',    desc: 'Security considered across architecture, development, testing, deployment, and operations.' },
  { icon: 'fa-desktop',      title: 'Responsive Web & Mobile',    desc: 'Consistent experiences across desktops, tablets, and smartphones.' },
  { icon: 'fa-chart-pie',      title: 'Rich User Experiences',    desc: 'Modern dashboards, intuitive workflows, interactive visualizations, and role-based interfaces.' },
  { icon: 'fa-layer-group',      title: 'Scalable Architecture',    desc: 'Systems designed to handle growing users, data, transactions, integrations, and programme requirements.' },
]

const GOV_TREE_BRANCH_IDS = ['egov-branch-1', 'egov-branch-2', 'egov-branch-3']

const GOV_EXPERIENCES = [
  { icon: 'fa-users',                title: 'The Citizen',        desc: 'Simple. Accessible. Multilingual. Responsive.' },
  { icon: 'fa-mobile-screen-button', title: 'The Field Team',     desc: 'Fast. Practical. Mobile-first. Easy to operate.' },
  { icon: 'fa-chart-line',           title: 'The Administrator',  desc: 'Insightful. Real-time. Data-driven. Actionable.' },
]

const VALUABLE_IMPACT = [
  { title: 'Energy & Utilities', desc: 'Dashboards and digital platforms for monitoring consumption, distribution, efficiency programmes, assets, and operational performance.' },
  { title: 'Programme Monitoring',            desc: 'Real-time monitoring of large-scale government programmes across states, districts, regions, offices, or implementing agencies.' },
  { title: 'Compliance & Reporting',   desc: 'Automated reporting, audit trails, role-based workflows, document management, and evidence-based monitoring.' },
  { title: 'Field Operations', desc: 'Digitization of surveys, inspections, verification, implementation, service delivery, and on-ground reporting.' },
  { title: 'AI-Enabled Administration',    desc: 'AI-assisted search, knowledge discovery, document processing, conversational interfaces, analytics, and workflow automation.' },
  { title: 'Citizen Services',    desc: 'Websites, portals, mobile applications, and digital services that make government information and services easier to access.' },
  { title: 'Scheme & Initiative Management',    desc: 'Technology platforms for managing beneficiaries, targets, milestones, approvals, implementation status, and programme outcomes.' },
  { title: 'Data-Driven Governance',    desc: 'Analytical platforms that consolidate data from multiple sources and transform it into actionable intelligence.' },
  
]
export default function EGovernance() {
  
 useTreeAnimation('egov-travel-dot', GOV_TREE_BRANCH_IDS, 600)
  return (
      <main id="main-content" className="page-wrapper">
        {/* <MetaInfo
    title="eGovernance Dashboards Development | CruxBytes"
    description="CruxBytes builds eGovernance platforms for government digitalization: real-time dashboards, field applications, analytics, multilingual websites, AI initiatives and long-term support."
    keywords="eGovernance, government digitalization, government dashboards, citizen services platform, field applications, multilingual government websites, CruxBytes"
    // url={`https://www.cruxbytes.com${ROUTES.EGOVERNANCE}`}
  /> */}
   <MetaInfo
     title={label}
      description={metaDesc}
      keywords=""
      />
         <div className="page-hero">
           <div className="bg-grid bg-grid--fill" aria-hidden="true" />
           <div className="container">
             {/* <div className="section-label">- Software Development -</div> */}
             <Breadcrumb />
             <h1 data-aos="fade-up" data-aos-delay={100}>
              {/* eGovernance Dashboards */}
              {label}
              </h1>
             <p className="page-subtitle" data-aos="fade-up" data-aos-delay={200}>
             {/* Digital Platforms That Turn Government Programmes Into Measurable Outcomes */}
             {pageDesc}
             </p>
             
             <div className="inner-page-actions">
               <Button  href={ROUTES.SALES_ENQUIRY}  variant="primary" icon="fa-arrow-right" iconPos="right" data-aos="fade-up" data-aos-delay={300}>
                 Get a Free Quote
               </Button>
              
             </div>
           </div>
         </div>
         {/* section 1 */}
         <section className="section-py bg-linear-gradient-light">
  <div className="container">
    <div className="sc-tm-row sc-tm-row--equal">
      <div className="sc-tm-content" data-aos="fade-left">
        <p>
          Government digitalization is not simply about putting a service online. It is about connecting programmes, departments, field teams, data, technology, and citizens through reliable digital platforms. These digital platforms are meant to bring transparency in these government programmes and turn them into measurable outcomes. It requires highly specialized expertise across security, accessibility, usability and governance considerations from modern government technology platforms.
        </p>
        <p>
          CruxBytes has ample experience developing technology solutions for government digitalization programmes — from real-time dashboards and field applications to analytical platforms, multilingual websites, AI-powered initiatives, regulatory platforms and long-term application support.
        </p>
      </div>

      <div>
        <ul className="diamond-gallery" role="list">
          {GOV_GALLERY.map(({ src, alt, row, col }, i) => (
            <li
              key={src}
              className="diamond-gallery-tile"
              tabIndex={0}
              style={{ '--tile-row': row, '--tile-col': col }}
             data-aos="fade-up" data-aos-delay={i * 50} 
            >
              <img src={src} alt={alt} width="400" height="400" loading="lazy" decoding="async"   
               />
            </li>
          ))}
        </ul>
      </div>
    </div>
     <ul className="stats-bar" role="list" data-aos="fade-up">
      {GOV_STATS.map(({ value, label }) => (
        <li key={label} className="impact-stat">
          <span className="impact-stat-value">{value}</span>
          <span className="impact-stat-label">{label}</span>
        </li>
      ))}
    </ul>
  </div>
        </section>
        {/* section 2 */}
        <section className="section-py " aria-labelledby="egovernance-journey-heading">
          <div className="container">
            <div className="section-header" data-aos="fade-up">
              <h2 id="egovernance-journey-heading">From Programme to Platform</h2>
              <p className='text-center'>We build the technology layer that connects these stages — helping government organizations convert large-scale programmes and processes into measurable, transparent, and technology-enabled operations. A successful eGovernance solution has many moving parts.</p>
            </div>

            <ScrollTimeline steps={GOV_JOURNEY} />
          </div>
        </section>

{/* section 3 */}
        <section className="section-py bg-peach" >
              <div className="container">
                    <div className="section-header pt-4 mb-4" data-aos="fade-up">
                      <h2 id="cto-capabilities-heading">What We Build for eGovernance</h2>              
                    </div>
                          <ul className="deliverables-grid--centered deliverables-grid--cols-5 flip-grid">
                  {EGOVERNANCE_BUILD.map((item, i) => (
                    <li
                      key={item.title}
                      className="flip-card"
                      tabIndex={0}
                      data-aos="fade-up"
                      data-aos-delay={(i % 3) * 70}
                    >
                      <div className="flip-card__inner">
                        <div className="flip-card__face">
                          {/* <span  aria-hidden="true">
                            <i className={`fa-solid ${item.icon}`} />
                          </span> */}
                          <div className="flip-card__title">{item.title}</div>
                        </div>
                        <div className="flip-card__face flip-card__face--back">
                          {/* <span  aria-hidden="true">
                            <i className={`fa-solid ${item.icon}`} />
                          </span> */}
                          <p className="flip-card__desc">{item.description}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                  </div>
          </section>

{/* section 4 */}
         <section className="section-py pt-0 section-card" aria-labelledby="egovernance-partner-heading" style={{ borderRadius:"0" }}>
          <div className="container">
            <div className="section-card" data-aos="fade-up">
              <div className="section-card__content">
                <h2 id="egovernance-partner-heading">
                  Looking for an apt partner for the Next Digital Governance Challenge?
                </h2>
                <p>
                  From a nationwide programme dashboard to a field-management application, from a multilingual government portal to an AI-enabled decision-support platform, CruxBytes brings together product design, software engineering, data, cloud, AI, security, and long-term application support.
                </p>
                <p>
                  We build digital platforms that help government programmes move from data to action, and from implementation to measurable outcomes.
                </p>
              </div>

              <div className="section-card__highlights">
                <h3>Our USP</h3>
                <ul className="section-card__list" role="list">
                  {GOV_PARTNER_USPS.map((item) => (
                    <li key={item} className="section-card__item">
                      <i className="fa-solid fa-circle-check" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button href={ROUTES.BE_A_PARTNER} variant="secondary" icon="fa-arrow-right" iconPos="right">
                Partner With Us
              </Button>
        
            </div>
          </div>
        </section>

       

{/* section 5 */}
<section className="section-py" aria-labelledby="pux-outcomes-heading">
  <div className="container">
    <div className="sc-ai-row sc-tm-row--sticky-heading">

      <div className="sc-tm-content" data-aos="fade-left">
         
     <h2 id="impact-heading" >
            <span className="impact-heading-dark">How Is It Different Than Other</span>
            <span className="impact-heading-light">Enterprise App Development</span>
          </h2>
          
      </div>
<div className="why-team-augmentation" data-aos="fade-right">
  <p>Government platforms often need to operate across multiple departments, locations, user groups, languages, devices, and levels of administration. Our development approach accounts for these realities through:</p>
  {DIFF_APP_DEVELOPMENT.map((card, i) => (
    <div
      key={card.title}
      className="why-team-augmentation-item"
      data-aos="fade-up"
      data-aos-delay={(i % 3) * 80}
    >
      <div className="why-team-augmentation-icon">
        <i className={`fa-solid ${card.icon}`} />
      </div>

      <div className="why-team-augmentation-content">
        <h3 className="why-team-augmentation-title">
          {card.title}
        </h3>

        <p className="why-team-augmentation-desc">
          {card.desc}
        </p>
      </div>
    </div>
  ))}
</div>  

    </div>
  </div>
</section>

{/* section 6 */}
<section className="section-py bg-linear-gradient-light text-center" aria-labelledby="egovernance-impact-heading">
  <div className="container">
    <div className="section-header mb-4" data-aos="fade-up">
      <h2 id="egovernance-impact-heading">Where eGovernance Technology Creates Impact</h2>
      <p>
        Government technology projects do not end when an application goes live.
      </p>
      <p className="mt-4">
        Whether a project is undertaken through a tender-based engagement, including procurement through platforms such as GeM, or through a direct technology engagement, successful delivery requires disciplined execution, documentation, security, support, and long-term ownership. GeM itself describes its technology platform as supporting transparent and efficient government procurement, including e-bidding and reverse auctions.
      </p>
    </div>

    <p className="text-bold" data-aos="fade-up" >
      The best eGovernance platforms bring together three experiences:
    </p>

    <div className="policy-tree-section">
      <div data-aos="fade-up" data-aos-delay={100}>
        <span className="policy-root">Effective Digital Governance</span>
      </div>

      <div className="policy-tree-connector" data-aos="fade-up" data-aos-delay={200}>
        <svg viewBox="0 0 1200 140" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <path
            id="egov-branch-1"
            className="branch-line"
            d="M 600,0 L 600,45 Q 600,70 550,70 L 212,70 Q 192,70 192,95 L 192,140"
          />
          <path
            id="egov-branch-2"
            className="branch-line"
            d="M 600,0 L 600,140"
          />
          <path
            id="egov-branch-3"
            className="branch-line"
            d="M 600,0 L 600,45 Q 600,70 650,70 L 988,70 Q 1008,70 1008,95 L 1008,140"
          />
          <circle className="travel-dot" id="egov-travel-dot" cx="600" cy="0" r="5" />
        </svg>
      </div>

      <ul className="policy-cards-row policy-cards-row--four policy-cards-row--three" role="list" data-aos="fade-up">
        {GOV_EXPERIENCES.map(({ icon, title, desc },i) => (
          <li className="policy-card" key={title} data-aos="fade-up" data-aos-delay={i * 100}>
            <span className="sc-bullet-icon" aria-hidden="true">
              <i className={`fa-solid ${icon}`} />
            </span>
            <span>{title}</span>
            <span className="hire-desc">{desc}</span>
          </li>
        ))}
      </ul>
    </div>

    <p className="section-subheading" data-aos="fade-up">
      We design and engineer platforms that balance all three — because effective digital governance depends on technology that works for everyone who uses it.
    </p>
    <p className="text-bold mt-4" data-aos="fade-up">
      The objective is simple: technology that continues to deliver value long after the initial implementation.
    </p>
  </div>
</section>
{/* section 7 */}
<section className="section-py" aria-labelledby="we-assess-heading">
      <div className="container">
        <div className="section-header" data-aos="fade-up">
          <h2 id="we-assess-heading">Areas where it creates a valuable impact</h2>
        </div>

      <div className="comparison-inner-table" role="table" aria-label="Comparison of other providers versus us" data-aos="fade-left">
       
          {VALUABLE_IMPACT.map(row => (
            <div className="comparison-inner-row" role="row" key={row.title}>
              <div className="comparison-inner-cell comparison-cell--title" role="cell">
                 <i class="fa-regular fa-thumbs-up  card-title-icon" aria-hidden="true"></i>
                 
                 <span className="comparison-title">{row.title}</span>
              </div>
              <div className="comparison-inner-cell comparison-cell--ours" role="cell">
                {row.desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

         <TransformBanner />
         </main>
  )
}